/** Scope export — v1 scope-of-work.js scopeExpRun / scopeExpPDF, ported.
 * Pure builders (buildScopeCsv / buildScopePdfHtml) so the numbers are tested;
 * the DOM wrappers (downloadCsv / openPrintable) are thin. Client-side only —
 * no writes, safe in Phase A. Per-item math is v1 verbatim: labor = q·labor,
 * material = q·material·(1+markup%), total = useCombined ? q·combined : l+m. */
import { SOW_SECTIONS, catTotals, itemMarkup, sowMatsBackfill, sowMatsOf, usd, type SowCat, type SowItem, type SowMatLink, type SowState } from "./api";

export type ExpPrices = "both" | "material" | "labor" | "none";

export interface ExpOpts {
  prices: ExpPrices;
  headers: boolean; // category header rows (PDF) / kept for parity
  subtotals: boolean; // per-category subtotal row (price row — skipped when prices="none")
  grand: boolean; // grand-total row (price row — skipped when prices="none")
  catIds: string[]; // selected category ids (in scope order)
  desc?: boolean; // include the item Description (desc + labor/material notes)
  materials?: boolean; // include linked materials (names ×qty) + their product links
  company?: string; // PDF header (right)
  footer?: string; // PDF footer notes / terms
  logo?: string; // optional company logo (downscaled data URL) — PDF header top-right
}

export const expShowLabor = (p: ExpPrices): boolean => p === "both" || p === "labor";
export const expShowMaterial = (p: ExpPrices): boolean => p === "both" || p === "material";
/** "None" means a priceless scope: NO Total column, NO subtotal/grand rows —
 * previously Total/subtotals still printed, which made "None" useless. */
export const expShowTotal = (p: ExpPrices): boolean => p !== "none";

/** minimal catalog shape used to backfill material links (sowMatsBackfill). */
export type ExpCatalog = { name: string; thumbnail: string | null; website: string | null }[];

/** The full description block for an item: its description plus any labor /
 * material notes — Tom's exports need everything written on the item. */
export function expItemDesc(it: SowItem): string {
  const parts: string[] = [];
  if (it.desc.trim()) parts.push(it.desc.trim());
  if (it.laborNotes.trim()) parts.push("Labor: " + it.laborNotes.trim());
  if (it.materialNotes.trim()) parts.push("Material: " + it.materialNotes.trim());
  return parts.join("\n");
}

/** An item's linked materials, with urls/imgs backfilled from the catalog. */
export function expItemMats(it: SowItem, catalog: ExpCatalog): SowMatLink[] {
  return sowMatsBackfill(sowMatsOf(it.raw), catalog);
}

const matLabel = (m: SowMatLink): string => m.name + ((m.qty || 1) > 1 ? ` ×${m.qty}` : "");

/** section sort index (SOW_SECTIONS order; unknown sections go last). */
const sectIdx = (key: string): number => {
  const i = SOW_SECTIONS.findIndex(([k]) => k === key);
  return i < 0 ? SOW_SECTIONS.length : i;
};
const sectLabel = (key: string): string => SOW_SECTIONS.find(([k]) => k === key)?.[1] ?? key;

/** categories the user selected, GROUPED by section (Exteriors, Interiors, …)
 * then in scope order within each — exports must not interleave sections. */
function selectedCats(sow: SowState, catIds: string[]): SowCat[] {
  const want = new Set(catIds);
  return sow.cats
    .filter((c) => want.has(c.id))
    .sort((a, b) => sectIdx(a.section) - sectIdx(b.section) || a.order - b.order);
}

interface Line { labor: number; material: number; total: number }
function itemLine(it: SowState["items"][number], sow: SowState): Line {
  const q = it.qty || 0;
  const labor = q * it.labor;
  const material = q * it.material * (1 + itemMarkup(it, sow.meta) / 100);
  const total = it.useCombined && it.combined ? q * it.combined : labor + material;
  return { labor, material, total };
}

// ---- Document model (pure — one shape both the PDF and printable HTML render) --

export interface ScopeDocItem {
  name: string;
  qtyLabel: string; // "1410 sqft"
  desc: string; // "" unless opts.desc
  mats: SowMatLink[]; // [] unless opts.materials
  labor: number | null; // null = column hidden by the price mode
  material: number | null;
  total: number | null;
}
export interface ScopeDocCat {
  name: string;
  items: ScopeDocItem[];
  subtotal: Line | null; // null when subtotals off or prices "none"
}
export interface ScopeDocSection {
  key: string;
  label: string; // "Exteriors" / "Interiors" / …
  cats: ScopeDocCat[];
  subtotal: Line | null;
}
export interface ScopeDoc {
  sections: ScopeDocSection[];
  grand: Line | null;
  showL: boolean;
  showM: boolean;
  showT: boolean;
}

/** Build the export document: categories grouped under their SECTION (Exteriors,
 * Interiors, … — never interleaved), items carrying description/materials per
 * the toggles, price fields per the price mode ("none" ⇒ every money field and
 * subtotal/grand is null). Both the real PDF and the printable HTML render this. */
export function buildScopeDoc(sow: SowState, opts: ExpOpts, catalog: ExpCatalog = []): ScopeDoc {
  const showL = expShowLabor(opts.prices);
  const showM = expShowMaterial(opts.prices);
  const showT = expShowTotal(opts.prices);
  const wantD = opts.desc === true;
  const wantMats = opts.materials === true;
  const sections: ScopeDocSection[] = [];
  const grand: Line = { labor: 0, material: 0, total: 0 };
  for (const c of selectedCats(sow, opts.catIds)) {
    let sect = sections[sections.length - 1];
    if (!sect || sect.key !== c.section) {
      sect = { key: c.section, label: sectLabel(c.section), cats: [], subtotal: opts.subtotals && showT ? { labor: 0, material: 0, total: 0 } : null };
      sections.push(sect);
    }
    const items: ScopeDocItem[] = [];
    for (const it of sow.items.filter((i) => i.catId === c.id && !i.isSub).sort((a, b) => a.order - b.order)) {
      const ln = itemLine(it, sow);
      items.push({
        name: it.name,
        qtyLabel: `${it.qty} ${it.unit}`,
        desc: wantD ? expItemDesc(it) : "",
        mats: wantMats ? expItemMats(it, catalog) : [],
        labor: showL ? ln.labor : null,
        material: showM ? ln.material : null,
        total: showT ? ln.total : null,
      });
      grand.labor += ln.labor; grand.material += ln.material; grand.total += ln.total;
      if (sect.subtotal) { sect.subtotal.labor += ln.labor; sect.subtotal.material += ln.material; sect.subtotal.total += ln.total; }
    }
    const ct = opts.subtotals && showT ? catTotals(sow.items, c.id, sow.meta) : null;
    sect.cats.push({ name: c.name, items, subtotal: ct });
  }
  return { sections, grand: opts.grand && showT ? grand : null, showL, showM, showT };
}

// ---- CSV --------------------------------------------------------------------

const csvCell = (v: string | number): string => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** v1 scopeExpRun CSV: header + item rows + optional per-cat subtotals + grand.
 * Money rounded to whole dollars, exactly like v1. Columns are dynamic:
 * Description and Materials/links ride along when requested, price columns
 * follow the price mode, and "none" produces a genuinely priceless sheet. */
export function buildScopeCsv(sow: SowState, opts: ExpOpts, catalog: ExpCatalog = []): string {
  const showL = expShowLabor(opts.prices);
  const showM = expShowMaterial(opts.prices);
  const showT = expShowTotal(opts.prices);
  const wantD = opts.desc === true;
  const wantMats = opts.materials === true;
  const head: string[] = ["Section", "Category", "Item"];
  if (wantD) head.push("Description");
  head.push("Qty", "Unit");
  if (wantMats) head.push("Materials", "Material links");
  const iL = showL ? head.push("Labor") - 1 : -1;
  const iM = showM ? head.push("Material") - 1 : -1;
  const iT = showT ? head.push("Total") - 1 : -1;
  const width = head.length;
  // a price-only row (subtotal / grand): label in the Item column, money in the
  // price columns, everything else blank — always exactly `width` cells wide.
  const priceRow = (label: string, l: number, m: number, t: number): (string | number)[] => {
    const r = new Array<string | number>(width).fill("");
    r[2] = label;
    if (iL >= 0) r[iL] = Math.round(l);
    if (iM >= 0) r[iM] = Math.round(m);
    if (iT >= 0) r[iT] = Math.round(t);
    return r;
  };
  const rows: (string | number)[][] = [head];
  let gl = 0, gm = 0, gt = 0;
  for (const c of selectedCats(sow, opts.catIds)) {
    for (const it of sow.items.filter((i) => i.catId === c.id && !i.isSub).sort((a, b) => a.order - b.order)) {
      const ln = itemLine(it, sow);
      const r: (string | number)[] = [c.section, c.name, it.name];
      if (wantD) r.push(expItemDesc(it));
      r.push(it.qty, it.unit);
      if (wantMats) {
        const mats = expItemMats(it, catalog);
        r.push(mats.map(matLabel).join("; "), mats.filter((m) => m.url).map((m) => m.url as string).join("; "));
      }
      if (showL) r.push(Math.round(ln.labor));
      if (showM) r.push(Math.round(ln.material));
      if (showT) r.push(Math.round(ln.total));
      rows.push(r);
      gl += ln.labor; gm += ln.material; gt += ln.total;
    }
    // subtotal is a PRICE row — with prices "none" there is nothing to print
    if (opts.subtotals && showT) {
      const ct = catTotals(sow.items, c.id, sow.meta);
      rows.push(priceRow(`${c.name} subtotal`, ct.labor, ct.material, ct.total));
    }
  }
  if (opts.grand && showT) rows.push(priceRow("GRAND TOTAL", gl, gm, gt));
  return rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
}

// ---- PDF (printable HTML) ---------------------------------------------------

const esc = (s: string): string =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** v1 scopeExpPDF: a self-contained printable HTML doc (the user prints it to
 * PDF). Same columns/rounding as the CSV: Description + materials (clickable
 * links) ride under the item name when requested, and "none" prints a truly
 * priceless scope (no Total column, no subtotal/grand rows). */
export function buildScopePdfHtml(sow: SowState, opts: ExpOpts, addr: string, catalog: ExpCatalog = []): string {
  const doc = buildScopeDoc(sow, opts, catalog);
  const { showL, showM, showT } = doc;
  const width = 2 + (showL ? 1 : 0) + (showM ? 1 : 0) + (showT ? 1 : 0); // all columns
  let body = "";
  for (const sect of doc.sections) {
    // SECTION band — Exteriors / Interiors / … are separated, never interleaved.
    body += `<tr><td colspan="${width}" style="padding:14px 6px 4px;font-weight:bold;font-size:13px;letter-spacing:.05em;border-bottom:2px solid #0f172a">${esc(sect.label.toUpperCase())}</td></tr>`;
    for (const c of sect.cats) {
      if (opts.headers) body += `<tr style="background:#1e293b;color:#fff"><td colspan="${width}" style="padding:8px;font-weight:bold">${esc(c.name)}</td></tr>`;
      for (const it of c.items) {
        // item cell: name, then the description block and the linked materials —
        // names ×qty, hyperlinked when they carry a URL.
        let cell = esc(it.name);
        if (it.desc) cell += `<div style="color:#64748b;font-size:11px;white-space:pre-line;margin-top:2px">${esc(it.desc)}</div>`;
        if (it.mats.length) {
          cell += `<div style="font-size:11px;margin-top:2px">${it.mats
            .map((m) => (m.url
              ? `<a href="${esc(m.url)}" style="color:#2563eb">${esc(matLabel(m))}</a>`
              : `<span style="color:#334155">${esc(matLabel(m))}</span>`))
            .join('<span style="color:#94a3b8"> · </span>')}</div>`;
        }
        body += `<tr><td style="padding:6px;border-bottom:1px solid #eee">${cell}</td><td style="padding:6px;border-bottom:1px solid #eee;vertical-align:top">${esc(it.qtyLabel)}</td>${it.labor !== null ? `<td style="padding:6px;text-align:right;border-bottom:1px solid #eee;vertical-align:top">${usd(it.labor)}</td>` : ""}${it.material !== null ? `<td style="padding:6px;text-align:right;border-bottom:1px solid #eee;vertical-align:top">${usd(it.material)}</td>` : ""}${it.total !== null ? `<td style="padding:6px;text-align:right;border-bottom:1px solid #eee;font-weight:bold;vertical-align:top">${usd(it.total)}</td>` : ""}</tr>`;
      }
      if (c.subtotal) body += `<tr><td colspan="${width - 1}" style="padding:6px;text-align:right;color:#64748b">${esc(c.name)} subtotal</td><td style="padding:6px;text-align:right;font-weight:bold">${usd(c.subtotal.total)}</td></tr>`;
    }
    if (sect.subtotal && sect.cats.length > 1) body += `<tr><td colspan="${width - 1}" style="padding:7px 6px;text-align:right;font-weight:bold;border-top:1px solid #cbd5e1">${esc(sect.label)} subtotal</td><td style="padding:7px 6px;text-align:right;font-weight:bold;border-top:1px solid #cbd5e1">${usd(sect.subtotal.total)}</td></tr>`;
  }
  if (doc.grand) body += `<tr style="background:#ecfdf5"><td colspan="${width - 1}" style="padding:8px;text-align:right;font-weight:bold">GRAND TOTAL</td><td style="padding:8px;text-align:right;font-weight:bold;color:#059669">${usd(doc.grand.total)}</td></tr>`;
  // Optional company logo, top-right of the header. It's a self-made canvas
  // data URL (≤160px), so it carries no HTML-special chars — safe to inline.
  const logoImg = opts.logo ? `<img src="${opts.logo}" alt="" style="max-height:52px;max-width:160px;display:block;margin:0 0 6px auto"/>` : "";
  return `<!doctype html><html><head><meta charset="utf-8"><title>Scope of Work</title></head><body style="font-family:Arial,sans-serif;padding:24px;color:#0f172a"><div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0f172a;padding-bottom:8px;margin-bottom:12px"><div><h1 style="margin:0;font-size:20px">Scope of Work</h1><div style="color:#64748b;font-size:12px">${esc(addr)}</div></div><div style="text-align:right;font-weight:bold">${logoImg}${esc(opts.company ?? "")}</div></div><table style="width:100%;border-collapse:collapse;font-size:12px"><thead><tr style="text-align:left;color:#64748b"><th style="padding:6px">Item</th><th style="padding:6px">Qty</th>${showL ? '<th style="padding:6px;text-align:right">Labor</th>' : ""}${showM ? '<th style="padding:6px;text-align:right">Material</th>' : ""}${showT ? '<th style="padding:6px;text-align:right">Total</th>' : ""}</tr></thead><tbody>${body}</tbody></table>${opts.footer ? `<p style="margin-top:16px;color:#64748b;font-size:11px">${esc(opts.footer)}</p>` : ""}</body></html>`;
}

// ---- Real PDF download (jsPDF, lazy-loaded) ----------------------------------

/** Build and DOWNLOAD an actual .pdf (no print dialog): renders the same
 * ScopeDoc the printable view uses — section bands (Exteriors / Interiors …),
 * category headers, descriptions, CLICKABLE material links, subtotals, grand
 * total, logo/company header, footer + page numbers. jsPDF is imported lazily
 * so the ~350KB library only loads when someone actually exports. */
export async function downloadScopePdf(sow: SowState, opts: ExpOpts, addr: string, catalog: ExpCatalog = []): Promise<void> {
  const { jsPDF } = await import("jspdf");
  const doc = buildScopeDoc(sow, opts, catalog);
  const pdf = new jsPDF({ unit: "pt", format: "letter" }); // 612×792
  const M = 40, R = 572, BOTTOM = 752;
  const slate = [15, 23, 42] as const, dim = [100, 116, 139] as const, blue = [37, 99, 235] as const;

  // column x's, packed from the right edge
  let cx = R;
  const xT = doc.showT ? cx : null; if (doc.showT) cx -= 62;
  const xM = doc.showM ? cx : null; if (doc.showM) cx -= 62;
  const xL = doc.showL ? cx : null; if (doc.showL) cx -= 62;
  const xQty = cx;
  const nameW = xQty - 70 - M;

  let y = 56;
  const ensure = (h: number): void => {
    if (y + h > BOTTOM) { pdf.addPage(); y = M + 8; }
  };

  // header: title + address left; logo + company right
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(18); pdf.setTextColor(...slate);
  pdf.text("Scope of Work", M, y);
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(...dim);
  pdf.text(addr, M, y + 15);
  let headBottom = y + 22;
  if (opts.logo) {
    try {
      const p = pdf.getImageProperties(opts.logo);
      const s = Math.min(90 / (p.width || 1), 30 / (p.height || 1), 1);
      const w = Math.max(1, (p.width || 1) * s), h = Math.max(1, (p.height || 1) * s);
      pdf.addImage(opts.logo, "PNG", R - w, 38, w, h);
      headBottom = Math.max(headBottom, 38 + h + 4);
      if (opts.company) { pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.setTextColor(...slate); pdf.text(opts.company, R, 38 + h + 14, { align: "right" }); headBottom = Math.max(headBottom, 38 + h + 18); }
    } catch { /* bad logo data — skip it */ }
  } else if (opts.company) {
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.setTextColor(...slate);
    pdf.text(opts.company, R, y, { align: "right" });
  }
  pdf.setDrawColor(...slate); pdf.setLineWidth(1.4);
  pdf.line(M, headBottom + 6, R, headBottom + 6);
  y = headBottom + 24;
  // column labels (once)
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(...dim);
  pdf.text("ITEM", M, y);
  pdf.text("QTY", xQty, y, { align: "right" });
  if (xL !== null) pdf.text("LABOR", xL, y, { align: "right" });
  if (xM !== null) pdf.text("MATERIAL", xM, y, { align: "right" });
  if (xT !== null) pdf.text("TOTAL", xT, y, { align: "right" });
  y += 12;

  for (const sect of doc.sections) {
    ensure(30);
    y += 10;
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(12); pdf.setTextColor(...slate);
    pdf.text(sect.label.toUpperCase(), M, y);
    pdf.setLineWidth(0.9); pdf.setDrawColor(...slate); pdf.line(M, y + 4, R, y + 4);
    y += 18;
    for (const c of sect.cats) {
      if (opts.headers) {
        ensure(24);
        pdf.setFillColor(30, 41, 59); pdf.rect(M, y - 11, R - M, 16, "F");
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(10); pdf.setTextColor(255, 255, 255);
        pdf.text(c.name, M + 6, y);
        y += 14;
      }
      for (const it of c.items) {
        pdf.setFontSize(10);
        const nameLines = pdf.splitTextToSize(it.name, nameW) as string[];
        pdf.setFontSize(8.5);
        const descLines = it.desc ? (pdf.splitTextToSize(it.desc, nameW) as string[]) : [];
        const h = nameLines.length * 12 + descLines.length * 10 + 8; // mats ensure themselves below
        ensure(Math.min(h, 700));
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(10); pdf.setTextColor(...slate);
        pdf.text(nameLines, M, y);
        // right-hand cells sit on the first line
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(...dim);
        pdf.text(it.qtyLabel, xQty, y, { align: "right" });
        if (xL !== null && it.labor !== null) pdf.text(usd(it.labor), xL, y, { align: "right" });
        if (xM !== null && it.material !== null) pdf.text(usd(it.material), xM, y, { align: "right" });
        if (xT !== null && it.total !== null) { pdf.setFont("helvetica", "bold"); pdf.setTextColor(...slate); pdf.text(usd(it.total), xT, y, { align: "right" }); }
        y += nameLines.length * 12;
        if (descLines.length) {
          pdf.setFont("helvetica", "normal"); pdf.setFontSize(8.5); pdf.setTextColor(...dim);
          pdf.text(descLines, M, y);
          y += descLines.length * 10;
        }
        for (const m of it.mats) {
          // WRAP long product names to the item column (they used to run off the
          // right edge of the page); the whole wrapped block stays clickable via
          // one link annotation laid over it.
          pdf.setFontSize(8.5);
          const matLines = pdf.splitTextToSize("• " + matLabel(m), nameW - 8) as string[];
          ensure(matLines.length * 10 + 2);
          if (m.url) {
            pdf.setTextColor(...blue);
            pdf.text(matLines, M + 4, y);
            const wMax = Math.min(nameW - 8, Math.max(...matLines.map((l) => pdf.getTextWidth(l))));
            pdf.link(M + 4, y - 8, wMax, matLines.length * 10, { url: m.url });
          } else {
            pdf.setTextColor(51, 65, 85);
            pdf.text(matLines, M + 4, y);
          }
          y += matLines.length * 10;
        }
        pdf.setDrawColor(226, 232, 240); pdf.setLineWidth(0.5); pdf.line(M, y - 4, R, y - 4);
        y += 8;
      }
      if (c.subtotal) {
        ensure(16);
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(...dim);
        pdf.text(`${c.name} subtotal`, xT !== null ? xT - 70 : R - 70, y, { align: "right" });
        pdf.setFont("helvetica", "bold"); pdf.setTextColor(...slate);
        pdf.text(usd(c.subtotal.total), R, y, { align: "right" });
        y += 15;
      }
    }
    if (sect.subtotal && sect.cats.length > 1) {
      ensure(18);
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(10); pdf.setTextColor(...slate);
      pdf.text(`${sect.label} subtotal`, xT !== null ? xT - 70 : R - 70, y, { align: "right" });
      pdf.text(usd(sect.subtotal.total), R, y, { align: "right" });
      y += 17;
    }
  }
  if (doc.grand) {
    ensure(28);
    pdf.setFillColor(236, 253, 245); pdf.rect(M, y - 12, R - M, 20, "F");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.setTextColor(...slate);
    pdf.text("GRAND TOTAL", xT !== null ? xT - 70 : R - 80, y + 2, { align: "right" });
    pdf.setTextColor(5, 150, 105);
    pdf.text(usd(doc.grand.total), R - 4, y + 2, { align: "right" });
    y += 24;
  }
  if (opts.footer) {
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(...dim);
    const fl = pdf.splitTextToSize(opts.footer, R - M) as string[];
    ensure(fl.length * 10 + 8);
    pdf.text(fl, M, y + 6);
  }
  // page numbers on every page
  const pages = pdf.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    pdf.setPage(i);
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(...dim);
    pdf.text(`Page ${i} of ${pages}`, R, 776, { align: "right" });
    pdf.text("Scope of Work — " + addr, M, 776);
  }
  pdf.save("scope-of-work.pdf");
}

// ---- DOM wrappers (thin; not unit-tested) -----------------------------------

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** v1's approach: open the HTML in a new tab and trigger the print dialog (the
 * user picks "Save as PDF"); if popups are blocked, fall back to downloading
 * the .html so it's never lost. Returns "print" | "download". */
export function openPrintable(html: string): "print" | "download" {
  const w = window.open("", "_blank");
  if (w) {
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch { /* user can print manually */ } }, 400);
    return "print";
  }
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "scope-of-work.html";
  a.click();
  URL.revokeObjectURL(url);
  return "download";
}
